/* eslint-disable rxjs/no-unsafe-switchmap */
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
    map, filter, tap, first, delay, timer, takeUntil, race,
    switchMap,
} from 'rxjs';

import { GA4EventType } from '../../models/general.model';
import * as GA4AnalyticsActions from '../ga4-analytics.actions';

import { ProductPublicModels } from '@features/products/product';
import { ProductsArrayPublicSelectors } from '@features/products/productsArray';
import { LoadCategorySuccess } from '@pages/category';
import { SetGridTypeCategoryGrid } from '@pages/category/modules/category-grid/store/category-grid.actions';
import { SetGridTypeCustomCategoryGrid } from '@pages/category/modules/grid-custom-category/store/custom-category-grid.actions';
import { getIsCategory } from '@pages/category/store/category-page.selectors';
import { getGridType } from '@pages/category/store/grid-type/grid-type.selectors';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { ViewItemService } from '@shared/analytics/COMMON/services/product-view/view-item.service';
import { HeaderEventAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { GridTypes } from '@shared/models/grid-type.model';

/** Si se quiere utilizar el sistema del ViewItemService para otra ubicación que no sea parrillas,
 * habría que generar otro archivo de efectos que trabaje con otra instancia del servicio
 * junto con un filtro según eventFrom en las reacciones al dispatch de Ga4ItemListVisible. */
@Injectable()
export class GA4ViewItemListParrillaEffects {
    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly actions$: Actions,
        private readonly router: Router,
        private readonly store: Store,
        private readonly viewItemService: ViewItemService,
    ) { }

    public EventViewItemListVisibleGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GA4AnalyticsActions.Ga4ItemListVisible),
            filter((action) => action.eventFrom === EventFrom.parrilla),
            map((event) => this.viewItemService.viewProduct(
                event.product,
                event?.inviewportElementId,
                event?.isPassthrough,
                event?.isReset
            )),
            filter((products) => !!products?.length),
            map((items: ProductPublicModels.PublicProduct[]) => {
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.view_item_list,
                    eventFrom: EventFrom.parrilla,
                    data: {
                        items,
                    },
                });
            })
        );
    });

    public ResetIsFirstTime$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategorySuccess, SetGridTypeCategoryGrid, SetGridTypeCustomCategoryGrid),
            tap(() => {
                this.viewItemService.resetIsFirstBatch();
            }),
        );
    }, { dispatch: false });

    /**
     * Efecto que se encarga de enviar el evento de analítica correspondiente con los productos que el usuario
     * vé nada más entra en una parrilla
     */
    public EventViewItemListStartGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategorySuccess, SetGridTypeCategoryGrid, SetGridTypeCustomCategoryGrid),
            concatLatestFrom(() => this.store.select(getIsCategory)),
            filter(([, _isCategory]) => _isCategory),
            concatLatestFrom(() => this.store.select(getGridType)),
            filter(([, gridType]) => gridType !== GridTypes.GRID_16),
            /** Después de que cargue una categoría o se cambie el tipo de grid se espera a que se se vean los primeros productos (ga4ItemListVisible) que se cargan nada
             * más llegar (200ms) y se envía evento de analítica con estos. Se ignora un posible evento con parámetro (isReset) porque no viene de la visualización de
             * un producto sinó del evento lanzado con los productos que faltaban por enviar en la categoría o tipo de parrilla anterior al hacer un cambio */
            switchMap(() => this.actions$.pipe(
                ofType(GA4AnalyticsActions.Ga4ItemListVisible),
                filter((action) => !action.isReset),
                first(),
                delay(200),
            )),
            map(() => GA4AnalyticsActions.Ga4ItemListVisible({
                eventFrom: EventFrom.parrilla,
                product: undefined,
                isPassthrough: true,
            })),
        );
    });

    /**
     * Efecto que se encarga de enviar el evento de analítica correspondiente cuando pasan (n) segundos desde
     * que se envió el último evento "view_item_list"
     */
    public EventViewItemListTimerGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GA4AnalyticsActions.Ga4Event),
            filter((event) => event.name === GA4EventType.view_item_list && event.eventFrom === EventFrom.parrilla),
            switchMap(() => timer(this.viewItemService.config.delayMilisecondsUntilEvent)),
            map(() => GA4AnalyticsActions.Ga4ItemListVisible({
                eventFrom: EventFrom.parrilla,
                product: undefined,
                isPassthrough: true,
            })),
            takeUntil(this.router.events.pipe(filter((event) => event instanceof NavigationStart))),
        );
    });

    /**
     * Efecto que se encarga de enviar el evento de analítica correspondiente cuando se navega fuera de una categoría
     */
    public EventViewItemListExitGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategorySuccess),
            switchMap(() => race([
                this.router.events.pipe(filter((event) => event instanceof NavigationStart)),
                /* Como el apartado de "Mi cuenta" es aún una página legacy no se utiliza el router para navegar y se recarga la página,
                por lo que para detectar que se navega fuera de la categoría en este caso por ahora necesitamos esta línea */
                this.actions$.pipe(ofType(HeaderEventAnalytics), filter((action) => action.section === 'account')),
            ])),
            concatLatestFrom(() => this.store.select(getIsCategory)),
            filter(([, _isCategory]) => _isCategory),
            map(() => GA4AnalyticsActions.Ga4ItemListVisible({
                eventFrom: EventFrom.parrilla,
                product: undefined,
                isPassthrough: true,
                isReset: true,
            })),
        );
    });

    /**
     * Efecto que se encarga de enviar el evento de analítica correspondiente cuando se cambia el filtro en una categoría
     */
    public EventViewItemListGridChangeGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetGridTypeCategoryGrid),
            concatLatestFrom(() => this.store.select(getIsCategory)),
            filter(([, _isCategory]) => _isCategory),
            map(() => GA4AnalyticsActions.Ga4ItemListVisible({
                eventFrom: EventFrom.parrilla,
                product: undefined,
                isPassthrough: true,
                isReset: true,
            })),
        );
    });

    /**
     * Efecto que se encarga de avisar de que se han cargado todos los productos de una parrilla para inciar
     * la observación de esta para detectar cuando el usuario llega al final
     */
    public EventViewItemListEndGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetGridTypeCategoryGrid, SetGridTypeCustomCategoryGrid),
            switchMap(() => this.store.select(ProductsArrayPublicSelectors.getProductsList)),
            filter((productsArray) => Object.values(productsArray).every((value) => !!value)),
            switchMap(() => this.actions$.pipe(
                ofType(GA4AnalyticsActions.Ga4ItemListVisible),
                filter((action) => !action.isReset),
                first(),
                delay(400),
            )),
            map(() => {
                const productListElem = this.document.getElementsByClassName('grid-product-list')[0];
                if (productListElem) {
                    this.viewItemService.actionWhenVisibleCustomObserver(
                        productListElem,
                        EventFrom.parrilla,
                        /** Utilizamos threshold a 0 junto con un rootMargin (márgen respecto al elemento padre) reducido al -100%
                         *  para detectar cuando se llega al final de la parrilla */
                        {
                            isOnce: true,
                            firstTimeViewTreshold: [0],
                            standardViewTreshold: [0],
                            rootMargin: '-100% 0px 0px 0px',
                        }
                    );
                }
                return productListElem;
            }),
            /* En el próximo cambio de categoría (o filtro de esta) se deja de observar el elemento de la parrilla, así evitamos que se observe
             * dos veces al realizar estas acciones de nuevo */
            switchMap((productListElem) => this.actions$.pipe(
                ofType(SetGridTypeCategoryGrid, SetGridTypeCustomCategoryGrid),
                tap(() => {
                    this.viewItemService.unobserve(productListElem);
                })
            ))
        );
    }, { dispatch: false });
}
